/**
 * Player-facing power-up surfaces (#232) — the phone hand + its companion cues.
 *
 * The server is the sole authority: these components only RENDER role-filtered state
 * (the viewer's own `tokens`, the clue's arm flags, the owner-only Sneak Peek hint /
 * Wager Boost roll / bite notice) and SEND a `play_powerup` command when a card is
 * tapped in its legal window. They never compute scores/legality beyond enabling the
 * button — the per-kind timing class (from {@link POWERUP_SPECS}) decides which window.
 *
 * v1 shipped ⏫ Double Up + 👀 Sneak Peek (pre-buzz). v2 adds 🎣 Steal (reopened
 * buzzer), ❄️ Brain Freeze + 🐍 Snake Bite (pre-buzz race cards), 🛡️ Insurance
 * (pre-buzz), 🎲 Wager Boost (wager step), and 📞 Tag-Team (post-buzz). Two cards are
 * targeted (Snake Bite's victim, Tag-Team's teammate) and open a small picker.
 */

import { useEffect, useState } from 'react';
import { POWERUP_KINDS, POWERUP_SPECS } from '@jeopardy/shared';
import type {
  PowerUpKind,
  PowerUpTiming,
  PowerUpTokens,
  SneakPeekWord,
} from '@jeopardy/shared';

/** Per-card presentation (icon/name/accent slug/effect blurb). */
export const POWERUP_CARD_META: Record<
  PowerUpKind,
  { icon: string; name: string; slug: string; fx: string }
> = {
  DOUBLE_UP: { icon: '⏫', name: 'Double Up', slug: 'double', fx: '2× · risk it' },
  SNEAK_PEEK: { icon: '👀', name: 'Sneak Peek', slug: 'peek', fx: 'hint, just you' },
  STEAL: { icon: '🎣', name: 'Steal', slug: 'steal', fx: 'grab the rebound' },
  BRAIN_FREEZE: {
    icon: '❄️',
    name: 'Brain Freeze',
    slug: 'freeze',
    fx: 'chill the room',
  },
  SNAKE_BITE: { icon: '🐍', name: 'Snake Bite', slug: 'snake', fx: 'bite one rival' },
  INSURANCE: { icon: '🛡️', name: 'Insurance', slug: 'insure', fx: 'half the loss' },
  WAGER_BOOST: { icon: '🎲', name: 'Wager Boost', slug: 'boost', fx: 'roll for more' },
  TAG_TEAM: { icon: '📞', name: 'Tag-Team', slug: 'tag', fx: 'call a friend' },
};

/** Short per-timing hint shown on a locked card so the player knows WHEN it plays. */
const TIMING_HINT: Record<PowerUpTiming, string> = {
  PRE_BUZZ: 'before the buzzer',
  REOPENED_BUZZ: 'after a miss',
  POST_BUZZ: 'while you answer',
  WAGER_STEP: 'Daily Double / Final',
};

/** A public play/grant juice event carries a monotonic `seq` so views spot a fresh one. */
interface SeqEvent {
  seq: number;
}

/**
 * Show `evt` for `ms` after it changes, then clear — the transient-animation primitive
 * behind the comeback celebration (player), the "PLAYER played CARD" slam (TV), the
 * "new card" glow (TV), and the v2 frozen/bitten/die cues. Re-fires only when a
 * genuinely new event object arrives. Pass `null` to keep it dormant.
 */
export function useFreshEvent<T extends SeqEvent>(evt: T | null, ms = 4000): T | null {
  const [shown, setShown] = useState<T | null>(null);
  useEffect(() => {
    if (!evt) return;
    setShown(evt);
    const timer = setTimeout(() => setShown(null), ms);
    return () => clearTimeout(timer);
  }, [evt, ms]);
  return shown;
}

/** A seat the viewer may target (Snake Bite victim / Tag-Team teammate). */
export interface PowerUpTarget {
  id: string;
  name: string;
}

export interface PowerUpHandProps {
  /** The viewer's OWN hand (public token counts). */
  tokens: PowerUpTokens;
  /** Cards this game's deck may hold (from `powerups.config.deck`). */
  deck: PowerUpKind[];
  /** Cards the viewer has armed on this clue (Double Up / Brain Freeze / Insurance / …). */
  armed: PowerUpKind[];
  /** Whether each timing class's play window is currently open. */
  windows: Record<PowerUpTiming, boolean>;
  /** Other seats the viewer may target (excludes self). Empty ⇒ targeted cards can't play. */
  targets: PowerUpTarget[];
  /** Send `play_powerup` for a tapped card (with a target for Snake Bite / Tag-Team). */
  onPlay: (kind: PowerUpKind, target?: string) => void;
}

/**
 * The hand of cards (mock 01/02/15). Each deck card shows its count; it is tappable only
 * inside its timing window while held (and not already armed). A targeted card opens an
 * inline picker of the other seats.
 */
export function PowerUpHand({
  tokens,
  deck,
  armed,
  windows,
  targets,
  onPlay,
}: PowerUpHandProps): React.JSX.Element | null {
  const [picking, setPicking] = useState<PowerUpKind | null>(null);
  // Only show cards the viewer actually HOLDS (or has armed this clue) — a tray full of
  // greyed-out cards they don't own is noise. A spent card drops out once it's gone.
  const cards = deck.filter(
    (k) => POWERUP_CARD_META[k] && ((tokens[k] ?? 0) > 0 || armed.includes(k)),
  );
  const enoughSeatsFor = (k: PowerUpKind): boolean =>
    targets.length + 1 >= (POWERUP_SPECS[k].minPlayers ?? 0);
  const anyPlayable = cards.some((k) => {
    const spec = POWERUP_SPECS[k];
    return (
      windows[spec.timing] &&
      (tokens[k] ?? 0) > 0 &&
      !armed.includes(k) &&
      enoughSeatsFor(k)
    );
  });
  const status = anyPlayable
    ? 'Tap to play'
    : armed.length > 0
      ? `${armed.length} armed`
      : 'Locked';

  const tap = (kind: PowerUpKind): void => {
    if (POWERUP_SPECS[kind].targeted) {
      setPicking((cur) => (cur === kind ? null : kind));
    } else {
      onPlay(kind);
    }
  };

  // Nothing held (all spent) → render nothing rather than an empty "Your cards" tray.
  if (cards.length === 0) return null;

  return (
    <div className="hand" data-testid="powerup-hand">
      <p className="hand__title">
        <span>Your cards</span>
        <span>{status}</span>
      </p>
      <div className={'hand__row' + (anyPlayable ? '' : ' hand--locked')}>
        {cards.map((kind) => {
          const meta = POWERUP_CARD_META[kind];
          const spec = POWERUP_SPECS[kind];
          const count = tokens[kind] ?? 0;
          const isArmed = armed.includes(kind);
          const needsTarget = spec.targeted && targets.length === 0;
          const minPlayers = spec.minPlayers ?? 0;
          const enoughSeats = enoughSeatsFor(kind);
          const canPlay =
            windows[spec.timing] && count > 0 && !isArmed && !needsTarget && enoughSeats;
          // A held card whose only block is the seat floor gets a clear reason (not the
          // generic timing hint), so a Tag-Team below 3 players reads "needs 3 players".
          const seatBlocked = count > 0 && !enoughSeats;
          const cls =
            `pcard pcard--${meta.slug}` +
            (isArmed ? ' pcard--armed' : '') +
            (!canPlay && !isArmed ? ' pcard--disabled' : '') +
            (picking === kind ? ' pcard--picking' : '');
          return (
            <button
              type="button"
              key={kind}
              className={cls}
              data-testid={`powerup-card-${kind}`}
              disabled={!canPlay}
              onClick={canPlay ? () => tap(kind) : undefined}
            >
              <span className="pcard__count">{count}</span>
              <span className="pcard__icon" aria-hidden="true">
                {meta.icon}
              </span>
              <span className="pcard__name">{meta.name}</span>
              <span className="pcard__fx">
                {canPlay || isArmed
                  ? meta.fx
                  : seatBlocked
                    ? `needs ${minPlayers} players`
                    : TIMING_HINT[spec.timing]}
              </span>
            </button>
          );
        })}
      </div>
      {picking && (
        <div className="target-picker" data-testid="powerup-target-picker">
          <p className="target-picker__title">
            {POWERUP_SPECS[picking].kind === 'SNAKE_BITE'
              ? '🐍 Bite whom? (they won’t know)'
              : '📞 Tag in a teammate'}
          </p>
          <div className="target-picker__row">
            {targets.map((t) => (
              <button
                type="button"
                key={t.id}
                className="target-pick"
                data-testid={`powerup-target-${t.id}`}
                onClick={() => {
                  onPlay(picking, t.id);
                  setPicking(null);
                }}
              >
                {t.name}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="target-picker__cancel"
            data-testid="powerup-target-cancel"
            onClick={() => setPicking(null)}
          >
            Cancel
          </button>
        </div>
      )}
      {!anyPlayable && (
        <div className="lock-note" data-testid="powerup-lock-note">
          🔒 Each card has its moment — Steal on a rebound, Wager Boost at the wager,
          Tag-Team while you answer, the rest before the buzzer
        </div>
      )}
    </div>
  );
}

/**
 * A PERSISTENT "you got a new card" banner (#232 v2). The 4s unlock pop is easy to miss —
 * grants often land BETWEEN rounds (no clue open → the hand isn't rendered) or in a batch
 * that clobbers the single last-grant event. This stays up (naming the new cards) until the
 * player opens a clue and can see the hand. Announced to assistive tech.
 */
export function NewCardsBanner({ kinds }: { kinds: PowerUpKind[] }): React.JSX.Element {
  const names = kinds.map((k) => POWERUP_CARD_META[k]?.name).filter(Boolean);
  return (
    <div
      className="new-cards"
      data-testid="new-cards-banner"
      role="status"
      aria-live="polite"
    >
      <span className="new-cards__icons" aria-hidden="true">
        {kinds.map((k) => (
          <span className="new-cards__icon" key={k}>
            {POWERUP_CARD_META[k]?.icon}
          </span>
        ))}
      </span>
      <span className="new-cards__txt">
        🎁 New card{kinds.length === 1 ? '' : 's'} in your hand: <b>{names.join(', ')}</b>{' '}
        — ready to play when the next clue opens.
      </span>
    </div>
  );
}

/** The Double Up "armed" banner (mock 02). Shown once the viewer is in `doubleUpBy`. */
export function DoubleUpArmedBanner(): React.JSX.Element {
  return (
    <div className="armed-banner" data-testid="doubleup-armed">
      ⏫ Double Up armed · win 2× · a miss costs the normal 1×
    </div>
  );
}

/** The Insurance "armed" banner (#232 v2) — parity with Double Up so the card's on. */
export function InsuranceArmedBanner(): React.JSX.Element {
  return (
    <div className="armed-banner armed-banner--insure" data-testid="insurance-armed">
      🛡️ Insurance armed · a wrong answer costs you half
    </div>
  );
}

/** Shown to a tagged teammate on THEIR phone (#232 v2) — "you're called in to help". */
export function TagTeamCalledNotice({
  fromName,
}: {
  fromName: string;
}): React.JSX.Element {
  return (
    <div className="tagteam-notice" data-testid="tagteam-called-notice">
      📞 {fromName} tagged you in — help them answer! If they nail it, you bank a bonus.
    </div>
  );
}

/**
 * The frozen-victim notice (#232 v2). Shown to every seat EXCEPT the Brain Freeze caster
 * while the freeze is live — this card is deliberately OPEN (the point is the tell).
 */
export function BrainFreezeNotice({ freezeMs }: { freezeMs: number }): React.JSX.Element {
  return (
    <div className="freeze-notice" data-testid="brain-freeze-notice">
      ❄️ Brain freeze! Your buzzer is chilled for {(freezeMs / 1000).toFixed(1)}s
    </div>
  );
}

/**
 * The Snake Bite reveal (#232 v2) — shown ONLY to a bitten target, the moment they try
 * to buzz and are blocked. Stealth until then (no advance warning); this is what makes
 * it clear WHY their buzzer failed. A bite whose target never buzzes stays silent forever.
 */
export function SnakeBiteNotice(): React.JSX.Element {
  return (
    <div className="snake-notice" data-testid="snake-bite-notice">
      🐍 You were bitten! A rival blocked your buzzer on this clue
    </div>
  );
}

export interface WagerBoostRollProps {
  /** The 1-based die face (carried on the wire); no longer rendered as a pip glyph. */
  face?: number;
  multiplier: number;
  wager: number;
}

/**
 * The Wager Boost result (#232 v2) — rolled AFTER the wager locks. Shows the win
 * multiplier and the boosted payout (`wager × multiplier`) the roller collects if they're
 * correct. Owner-only. Rendered from the PERSISTENT, clue-scoped `snapshot.wagerBoost`
 * (item 4), so it stays up until the clue / Final wager ends — no die glyph.
 */
export function WagerBoostRoll({
  multiplier,
  wager,
}: WagerBoostRollProps): React.JSX.Element {
  const payout = Math.round(wager * multiplier);
  const mult = Number.isInteger(multiplier) ? `${multiplier}` : multiplier.toFixed(2);
  return (
    <div className="boost-roll" data-testid="wager-boost-roll">
      <div className="boost-roll__title">🎲 Wager Boost — {mult}× multiplier!</div>
      <div className="boost-roll__sub">
        ${wager.toLocaleString()} × {mult} = <b>${payout.toLocaleString()}</b> if
        you&rsquo;re right
      </div>
    </div>
  );
}

export interface SneakPeekHintProps {
  prefix: string;
  words: SneakPeekWord[];
}

/**
 * The private Sneak Peek hint (mock 14): the response prefix ("What is / Who is") then a
 * row of per-word slots — the first letter, then `length - 1` blanks. Rendered ONLY from
 * the viewer's own `sneak_peek_revealed`; never another player's.
 */
export function SneakPeekHintView({
  prefix,
  words,
}: SneakPeekHintProps): React.JSX.Element {
  return (
    <div className="peek-hint" data-testid="sneak-peek-hint">
      <div className="peek-hint__title">👀 Sneak Peek</div>
      <div className="peek-hint__sub">A tiny nudge before you decide to buzz.</div>
      <div className="peek-hint__prefix">{prefix}…</div>
      <div className="peek-words">
        {words.map((w, wi) => (
          <span className="peek-word" key={wi}>
            <span className="peek-slot">{w.first}</span>
            {Array.from({ length: Math.max(0, w.length - 1) }).map((_, si) => (
              <span className="peek-slot peek-slot--blank" key={si}>
                _
              </span>
            ))}
          </span>
        ))}
      </div>
      <div className="peek-hint__meta">
        {words.length} word{words.length === 1 ? '' : 's'} · first letter of each shown
      </div>
      <div className="peek-private">🔒 Only you can see this</div>
    </div>
  );
}

/**
 * Comeback-grant celebration (mock 05). Deliberately says only "you unlocked a card" —
 * never the deficit reason (D4: show the tool, hide the reason). The caller controls how
 * long it stays up (see {@link useFreshEvent}).
 */
export function ComebackUnlock({ powerup }: { powerup: PowerUpKind }): React.JSX.Element {
  const meta = POWERUP_CARD_META[powerup];
  return (
    <div className="unlock" data-testid="powerup-unlock">
      <div className="unlock__spark" aria-hidden="true" />
      <div className="unlock__eyebrow">✦ Nice buzz — reward unlocked</div>
      <div className={`unlock__card unlock__card--${meta.slug}`}>
        <div className="pcard__icon" aria-hidden="true">
          {meta.icon}
        </div>
        <div className="pcard__name">{meta.name}</div>
      </div>
      <div className="unlock__title">You unlocked {meta.name}!</div>
      <div className="unlock__sub">Play it in its moment to use it.</div>
    </div>
  );
}

/** Exported for reuse: the runtime deck order when a game doesn't narrow it. */
export const ALL_POWERUP_KINDS = POWERUP_KINDS;
